import { useEffect, useState } from 'react'
import { Navigate, NavLink, Outlet } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ApiError, api, type MeResponse } from '../api/client'
import { resolveTheme } from '../lib/theme'
import { ThemeSwitch } from '../editorial/ThemeSwitch'
import { deskFooterLink } from '../desk/nav'
import { getSettings } from './api'
import { cdHref, cdNav, mayUseCampaignDesk } from './nav'
import { focus } from './ui'

/** The Campaign Desk shell: its own full-screen area, styled like the Subscriber Desk
 *  (the same editorial_theme preference). Gone when the desk is switched off (404). */
export function CampaignDeskLayout() {
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/api/me'), staleTime: 60_000, retry: false })
  const allowed = mayUseCampaignDesk(me?.role)
  const probe = useQuery({ queryKey: ['campaign-desk', 'settings'], queryFn: getSettings, enabled: allowed, retry: false, staleTime: 30_000 })
  const [systemDark, setSystemDark] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches)
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)')
    if (!mq?.addEventListener) return
    const on = (e: MediaQueryListEvent) => setSystemDark(e.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  const pref = me?.preferences?.editorial_theme ?? 'light'
  const theme = resolveTheme(pref, systemDark)
  useEffect(() => {
    if (!me) return
    document.documentElement.dataset.theme = theme
    return () => { delete document.documentElement.dataset.theme }
  }, [me, theme])
  if (!me) return null
  if (!allowed) return <Navigate to="/" replace />
  if (probe.error instanceof ApiError && (probe.error.status === 404 || probe.error.status === 403)) return <Navigate to="/" replace />
  const back = deskFooterLink(me.role)
  const agentOn = probe.data?.agent_on

  return (
    <div data-theme={theme} className="min-h-screen bg-ink-900 text-paper font-ui text-[14px] md:flex">
      <nav aria-label="Campaign Desk" className="flex w-full shrink-0 flex-col gap-6 border-b border-ink-500 bg-ink-700 px-3.5 pb-7 pt-6 md:min-h-screen md:w-[264px] md:border-b-0 md:border-r">
        <div className="flex flex-col gap-0.5 px-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-amber-glow">AAMCirc</span>
          <span className="font-serif text-[23px] font-medium tracking-tight">Campaign Desk</span>
          {agentOn !== undefined && (
            <span className={`mt-1 text-[12px] font-semibold ${agentOn ? 'text-radar-green' : 'text-paper-faint'}`}>
              Agent {agentOn ? 'on' : 'off'}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-1">
          {cdNav().map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === cdHref()}
                     className={({ isActive }) => `rounded-lg px-2.5 py-[9px] font-semibold text-paper ${focus} ${isActive ? 'bg-ink-600 ring-1 ring-ink-400' : 'hover:bg-ink-600'}`}>
              {n.label}
            </NavLink>
          ))}
        </div>
        <div className="mt-auto flex flex-col gap-3 border-t border-ink-500 px-2.5 pt-3.5 text-[13px]">
          <span className="text-paper-faint">Signed in as <strong className="break-all font-semibold text-paper">{me.email}</strong></span>
          <ThemeSwitch value={pref} />
          {back && <NavLink to={back.to} className={`text-amber-glow underline underline-offset-2 hover:text-paper ${focus}`}>{back.label}</NavLink>}
        </div>
      </nav>
      <main className="min-w-0 flex-1 px-4 py-6 md:px-8">
        <Outlet />
      </main>
    </div>
  )
}
